import { ReactElement, useEffect, useRef, useState } from "react";
import { SciChartReact } from "scichart-react";
import { SciChartSurface } from "scichart/Charting/Visuals/SciChartSurface";
import { MouseWheelZoomModifier } from "scichart/Charting/ChartModifiers/MouseWheelZoomModifier";
import { ZoomExtentsModifier } from "scichart/Charting/ChartModifiers/ZoomExtentsModifier";
import { SciChartJSLightTheme } from "scichart/Charting/Themes/SciChartJSLightTheme";
import { ZoomPanModifier } from "scichart/Charting/ChartModifiers/ZoomPanModifier";
import { EAxisAlignment } from "scichart/types/AxisAlignment";
import { NumericAxis } from "scichart/Charting/Visuals/Axis/NumericAxis";
import { 
    AnnotationDragDeltaEventArgs, 
    EExecuteOn, 
    ELabelPlacement, 
    EXyDirection, 
    FastBandRenderableSeries, 
    HorizontalLineAnnotation, 
    ModifierMouseArgs, 
    NumberRange, 
    RubberBandXyZoomModifier, 
    XyyDataSeries 
} from "scichart";

interface Props {
    threshold: Array<{id: number, name: string, value: number, colour: string, unit: string}>
    volume: number
    updateChart: (item) => void;
    updateVolume: (item) => void;
    checked: boolean
}

class HorizontalLine extends HorizontalLineAnnotation {
    public lineThreshold: number = 0;
    public lower: number = 0;
    public upper: number = Number.MAX_VALUE;

    public onDragAdorner(args: ModifierMouseArgs): void {
        const xyValues = this.getValuesFromCoordinates(args.mousePoint, true);
        const newY = xyValues.y;
        
        // Apply constraints
        if (newY > this.upper) {
            this.y1 = this.upper;
        } else if (newY < this.lower) {
            this.y1 = this.lower;
        } else {
            this.y1 = newY;
        }
        
        this.dragDelta.raiseEvent(new AnnotationDragDeltaEventArgs());
    }
}

export default function ThresholdChart(props: Props): ReactElement {
    const [chartKey, setChartKey] = useState(0);
    const annotationsRef = useRef<HorizontalLine[]>([]);
    const volumeLineRef = useRef<HorizontalLine | null>(null);

    const updateBands = (sciChartSurface: SciChartSurface, wasmContext: any, thresholds: typeof props.threshold) => {
        // Clear existing series
        sciChartSurface.renderableSeries.clear();
        
        // Create band series data
        const allLines = thresholds.map(x => [x.value, x.value]);
        const maxValue = Math.max(...thresholds.map(x => x.value));
        allLines.unshift([maxValue * 1.8, maxValue * 1.8]);

        // Create bands
        for (let i = 0; i < thresholds.length; i++) {
            const dataSeries = new XyyDataSeries(wasmContext);
            dataSeries.appendRange([0, 1], allLines[i + 1], allLines[i]);
            
            const bandSeries = new FastBandRenderableSeries(wasmContext, {
                fill: thresholds[i].colour,
                stroke: "transparent",
                strokeY1: "transparent",
                isDigitalLine: true
            });
            bandSeries.dataSeries = dataSeries;
            sciChartSurface.renderableSeries.add(bandSeries);
        }

        // Update y-axis range
        const yAxis = sciChartSurface.yAxes.get(0);
        if (yAxis) {
            yAxis.visibleRange = new NumberRange(0, maxValue * 1.8);
        }

        sciChartSurface.invalidateElement();
    };

    const initChart = async (rootElement: string | HTMLDivElement) => {
        const theme = {...new SciChartJSLightTheme()};
        theme.loadingAnimationForeground = "rgba(255, 54, 48, 1)";
        theme.loadingAnimationBackground = '#FAFAFA';

        const { sciChartSurface, wasmContext } = await SciChartSurface.create(
            typeof rootElement === 'string' ? document.getElementById(rootElement) as HTMLDivElement : rootElement,
            { theme }
        );

        // Setup axes
        const xAxis = new NumericAxis(wasmContext, {
            drawMajorBands: false,
            drawMinorGridLines: false,
            drawMajorGridLines: false,
            isVisible: false,
            visibleRange: new NumberRange(0, 1),
            growBy: new NumberRange(0.05, 0.05),
            labelStyle: { fontFamily: "Roboto", fontSize: 12 }
        });

        const yAxis = new NumericAxis(wasmContext, {
            axisAlignment: EAxisAlignment.Right,
            drawMajorBands: false,
            drawMinorGridLines: false,
            drawMajorGridLines: true,
            maxAutoTicks: 5,
            labelPrecision: 2,
            visibleRange: new NumberRange(0, Math.max(...props.threshold.map(x => x.value)) * 1.8),
            zoomExtentsToInitialRange: true,
            labelStyle: { fontFamily: "Roboto", fontSize: 12 }
        });

        sciChartSurface.xAxes.add(xAxis);
        sciChartSurface.yAxes.add(yAxis);

        // Add chart modifiers
        sciChartSurface.chartModifiers.add(
            new ZoomPanModifier({ xyDirection: EXyDirection.XDirection }),
            new MouseWheelZoomModifier({ xyDirection: EXyDirection.XDirection }),
            new ZoomExtentsModifier(),
            new RubberBandXyZoomModifier({ executeOn: EExecuteOn.MouseRightButton })
        );

        // Add threshold lines
        annotationsRef.current = [];
        for (let i = 0; i < props.threshold.length - 1; i++) {
            const threshold = props.threshold[i];
            const nextThreshold = props.threshold[i + 1];
            const prevThreshold = i > 0 ? props.threshold[i - 1] : null;

            const line = new HorizontalLine({
                labelPlacement: ELabelPlacement.Axis,
                showLabel: true,
                stroke: threshold.colour,
                strokeThickness: 2,
                y1: threshold.value,
                x1: 0,
                axisLabelFill: threshold.colour,
                isEditable: !props.checked && i !== 2,
                onDragStarted: () => {
                    line.lineThreshold = threshold.value;
                    line.lower = nextThreshold.value + 0.1;
                    line.upper = prevThreshold ? prevThreshold.value - 0.1 : Number.MAX_VALUE;
                },
                onDragEnded: () => {
                    if (!props.checked && i !== 2) {
                        const newThresholds = [...props.threshold];
                        newThresholds[i].value = line.y1;
                        props.updateChart(newThresholds);
                    }
                }
            });

            sciChartSurface.annotations.add(line);
            annotationsRef.current.push(line);
        }

        // Add bands
        updateBands(sciChartSurface, wasmContext, props.threshold);

        // Add volume line
        const volumeLine = new HorizontalLine({
            labelPlacement: ELabelPlacement.TopLeft,
            showLabel: true,
            stroke: 'rgba(50,53,67,1)',
            strokeThickness: 4,
            y1: props.volume,
            x1: 0,
            axisLabelFill: 'rgba(50,53,67,1)',
            isEditable: true,
            onDragEnded: () => {
                props.updateVolume(volumeLine.y1);
            }
        });
        sciChartSurface.annotations.add(volumeLine);
        volumeLineRef.current = volumeLine;

        return { sciChartSurface, wasmContext };
    };

    // Update chart when props change
    useEffect(() => {
        setChartKey(prev => prev + 1);
    }, [props.threshold, props.volume, props.checked]);

    return (
        <div style={{ height: '100%', marginBottom: '5px' }}>
            <SciChartReact
                key={chartKey}
                initChart={initChart}
                style={{ width: '100%', height: '100%' }}
                fallback={<div>Loading...</div>}
            />
        </div>
    );
}
